import Sidebar from "../sidebar/sidebar";
import styled from "./style.module.css";
import { Outlet } from "react-router-dom";
import Searchbar from "../searchbar/searchbar";
import CommonHeader from "../CommonHeader/CommonHeader";
import CreatorSignupDetails from "./CreatorSignupDetails";
import { useEffect, useState } from "react";
import { startTokenRefresher } from "../../utils/RefreshToken";
import { creatorGetProfile } from "../../api/HttpApis";

// NOTE: the upload tray is deliberately NOT mounted here any more. Several routes
// (/file-upload, /video/:id, /Addseries, /creator-onboarding) render outside this layout,
// and those are the Add-Content screens people upload from — so a tray mounted here was
// invisible on exactly the pages that needed it. It now lives in App.js inside
// ProgressProvider, which covers every route. See GlobalUploadTray.

// A creator can now log in regardless of their admin-review status (the backend
// no longer blocks login for Pending/Rejected/etc.). Until they're Approved,
// every /mainlayout route renders this notice instead of the real dashboard —
// there's nothing to manage yet, so no sidebar/content makes sense either.
const CREATOR_STATUS_MESSAGES = {
  Pending: "Your profile is currently under review.",
  "Pending Review": "Your profile is currently under review.",
  Rejected: "Your application was rejected. Contact support for details.",
  "Changes Requested": "Your application needs changes — see the details below.",
  Inactive: "Your account has been deactivated. Contact an administrator.",
};
// Any status other than "Approved" blocks the dashboard. A status without an
// entry above (a new/renamed backend value) still needs to block — otherwise
// an unmapped status would silently fall through to the full dashboard
// instead of this notice.
const DEFAULT_CREATOR_STATUS_MESSAGE =
  "Your account isn't currently active. Contact an administrator for details.";

// Status pill tone for the review hero below.
const CREATOR_STATUS_TONE = {
  Pending: "statusPending",
  "Pending Review": "statusPending",
  "Changes Requested": "statusChanges",
  Rejected: "statusRejected",
  Inactive: "statusRejected",
};

// Header card on the under-review page: channel cover image (gradient when
// none was uploaded), overlapping avatar, name/channel, status pill and the
// status message.
const CreatorReviewHero = ({ profile, status, message }) => {
  const banner = profile?.channel?.channel_banner;
  const channelName = profile?.channel?.channel_name;
  const name = profile?.name || "";
  return (
    <div className={styled.reviewHero}>
      <div className={styled.reviewHeroBanner}>
        {/* A banner URL that 404s would otherwise draw an empty box with the
            alt text spilling out; hide the element instead. */}
        {banner && (
          <img
            src={banner}
            alt={`${channelName || name} cover`}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        )}
      </div>
      <div className={styled.reviewHeroBody}>
        <div className={styled.reviewHeroAvatar}>
          {/* The initial sits underneath; a failed image hides itself and
              reveals it, so a dead URL looks the same as no URL. */}
          <span>{(name || channelName || "?").charAt(0).toUpperCase()}</span>
          {profile?.profile_img && (
            <img
              src={profile.profile_img}
              alt={name}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
        </div>
        <div className={styled.reviewHeroInfo}>
          <div className={styled.reviewHeroNameRow}>
            <h2 className={styled.reviewHeroName}>{channelName || name}</h2>
            {status && (
              <span
                className={`${styled.reviewHeroStatus} ${
                  styled[CREATOR_STATUS_TONE[status] || "statusPending"]
                }`}
              >
                {status}
              </span>
            )}
          </div>
          {channelName && name && <p className={styled.reviewHeroSub}>{name}</p>}
        </div>
      </div>
      <p className={styled.reviewHeroMessage}>{message}</p>
    </div>
  );
};

const readCachedCreatorStatus = () => {
  if (localStorage.getItem("role") !== "Creator") return null;
  try {
    const userInfo = JSON.parse(localStorage.getItem("userInfo") || "null");
    return userInfo?.user?.status || null;
  } catch {
    return null;
  }
};

const MainLayout = () => {
  // Token refresher
  useEffect(() => {
    setTimeout(() => startTokenRefresher(), 5000);
  }, []);

  const isCreator = localStorage.getItem("role") === "Creator";

  // Seeded from the login-time snapshot for an instant first paint, then
  // refreshed from the server. Without the refresh, an admin approving,
  // rejecting, or requesting changes while the creator is already logged in
  // would never show up here — this page would keep rendering whatever
  // status was cached at their last login until they logged out and back in.
  const [creatorStatus, setCreatorStatus] = useState(readCachedCreatorStatus);
  // Full profile fetched once here and handed down to CreatorSignupDetails so
  // it doesn't issue its own duplicate creatorGetProfile() request.
  const [creatorProfile, setCreatorProfile] = useState(null);
  // True only while we have no cached status AND the initial fetch hasn't
  // resolved yet. Without this, a creator with no cached status (e.g. status
  // was added to their profile after they last logged in) would fall through
  // the `creatorStatus &&` check below and see the full dashboard for a beat
  // before the fetch comes back and (maybe) yanks it away.
  const [statusLoading, setStatusLoading] = useState(isCreator && !creatorStatus);

  // Lightweight refetch for after an in-page action changes the profile
  // (e.g. capturing a signature) — just refreshes the cached profile object,
  // not the status-caching/localStorage dance the initial load below does.
  const refreshCreatorProfile = () => {
    creatorGetProfile()
      .then((res) => {
        const profile = res?.data?.data;
        if (profile) setCreatorProfile(profile);
      })
      .catch(() => {
        /* keep the current profile on failure */
      });
  };

  useEffect(() => {
    if (!isCreator) return;
    let cancelled = false;
    creatorGetProfile()
      .then((res) => {
        const profile = res?.data?.data;
        const status = profile?.status;
        if (cancelled) return;
        if (profile) setCreatorProfile(profile);
        if (!status) return;
        setCreatorStatus(status);
        try {
          const userInfo = JSON.parse(localStorage.getItem("userInfo") || "null");
          if (userInfo?.user) {
            userInfo.user.status = status;
            localStorage.setItem("userInfo", JSON.stringify(userInfo));
          }
        } catch {
          /* ignore malformed cache */
        }
      })
      .catch(() => {
        /* keep the cached status on failure */
      })
      .finally(() => {
        if (!cancelled) setStatusLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Still waiting on the very first status check with nothing cached to show
  // in the meantime — render neither the notice nor the dashboard yet.
  if (statusLoading && !creatorStatus) {
    return (
      <div>
        <CommonHeader />
        <div className={styled.reviewOnlyWrap}>
          <div className={styled.reviewOnlyCol}>
            <div className={styled.reviewOnlyCard}>Loading your account status…</div>
          </div>
        </div>
      </div>
    );
  }

  const creatorStatusMessage =
    creatorStatus && creatorStatus !== "Approved"
      ? CREATOR_STATUS_MESSAGES[creatorStatus] || DEFAULT_CREATOR_STATUS_MESSAGE
      : null;

  // An unapproved creator gets no sidebar/dashboard at all — just this notice
  // (with the header's avatar menu for Logout) until an admin approves them.
  if (creatorStatusMessage) {
    return (
      <div>
        <CommonHeader />
        <div className={styled.reviewOnlyWrap}>
          <div className={styled.reviewOnlyCol}>
            <CreatorReviewHero
              profile={creatorProfile}
              status={creatorStatus}
              message={creatorStatusMessage}
            />
            <CreatorSignupDetails profile={creatorProfile} onRefresh={refreshCreatorProfile} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styled.dashboardContainer}>
      <Sidebar />
      <div className={styled.contentArea}>
        <Searchbar />
        <Outlet />
      </div>
    </div>
  );
};

export default MainLayout;
